import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CategoryDTO, CreateCategoryDTO, CategoryState, CategoryPaginationResponse } from '../../types/category';
import { createCategoryAPI, deleteCategoryAPI, fetchCategoriesAPI, updateCategoryAPI } from './categoryAPI';
import { RootState } from '../../app/store';

const initialState: CategoryState = {
  items: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 0,
  totalItems: 0,
  itemsPerPage: 10
};

export const fetchCategories = createAsyncThunk(
  'category/fetchCategories',
  async ({ page, limit, search, status }: { page?: number; limit?: number; search?: string; status?: string }, { rejectWithValue }) => {
    try {
      const response = await fetchCategoriesAPI(page, limit, search, status);
      if (!response.status) {
        return rejectWithValue(response);
      }
      
      // The backend ResponseInterceptor puts the Category[] in response.data
      // and pagination metadata in response.pagination (top-level on ApiResponse)
      return {
        data: Array.isArray(response.data) ? response.data : [],
        pagination: response.pagination,
      } as CategoryPaginationResponse;
    } catch (error) {
      return rejectWithValue({ status: false, message: 'Network error occurred' });
    }
  }
);

export const createCategory = createAsyncThunk(
  'category/createCategory',
  async ({ category, imageFile }: { category: CreateCategoryDTO; imageFile?: File | null }, { rejectWithValue }) => {
    const response = await createCategoryAPI(category, imageFile);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return response.data;
  }
);

export const updateCategory = createAsyncThunk(
  'category/updateCategory',
  async ({ uuid, category, imageFile }: { uuid: string; category: Partial<CreateCategoryDTO>; imageFile?: File | null }, { rejectWithValue }) => {
    const response = await updateCategoryAPI(uuid, category, imageFile);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return response.data;
  }
);

export const deleteCategory = createAsyncThunk(
  'category/deleteCategory',
  async (uuid: string, { rejectWithValue }) => {
    const response = await deleteCategoryAPI(uuid);
    if (!response.status) {
      return rejectWithValue(response);
    }
    return uuid;
  }
);

const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    resetCategory: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalPages = 0;
      state.totalItems = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch categories
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        if (action.payload) {
          state.loading = false;
          
          // Handle backend response format: { data: CategoryDTO[], pagination: PaginationMeta }
          const payload = action.payload as CategoryPaginationResponse;
          if (payload.data && Array.isArray(payload.data) && payload.pagination) {
            // Backend pagination response
            state.items = payload.data;
            state.currentPage = payload.pagination.currentPage;
            state.totalPages = payload.pagination.totalPages;
            state.totalItems = payload.pagination.totalItems;
            state.itemsPerPage = payload.pagination.pageSize;
          } else if (Array.isArray(payload)) {
            // Direct array fallback
            state.items = payload;
            state.currentPage = 1;
            state.totalPages = 1;
            state.totalItems = payload.length;
            state.itemsPerPage = 10;
          } else {
            state.items = [];
          }
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to fetch categories';
        } else {
          state.error = action.error.message ?? 'Failed to fetch categories';
        }
      })
      // Create category
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to create category';
        } else {
          state.error = action.error.message ?? 'Failed to create category';
        }
      })
      // Update category
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const updatedCategory = action.payload as CategoryDTO;
          state.items = state.items.map(item => 
            item.uuid === updatedCategory.uuid ? updatedCategory : item
          );
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to update category';
        } else {
          state.error = action.error.message ?? 'Failed to update category';
        }
      })
      // Delete category
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        const uuid = action.payload as string;
        state.items = state.items.filter(item => item.uuid !== uuid);
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const errorResponse = action.payload as any;
          state.error = errorResponse.message || 'Failed to delete category';
        } else {
          state.error = action.error.message ?? 'Failed to delete category';
        }
      });
  },
});

export const selectCategories = (state: RootState) => state.category.items;
export const selectCategoryLoading = (state: RootState) => state.category.loading;
export const selectCategoryError = (state: RootState) => state.category.error;
export const selectCategoryPagination = (state: RootState) => ({
  currentPage: state.category.currentPage,
  totalPages: state.category.totalPages,
  totalItems: state.category.totalItems,
  itemsPerPage: state.category.itemsPerPage,
});

export default categorySlice.reducer;
